import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  ReactNode,
} from 'react';
import { Overlay } from 'react-bootstrap';
import type { Placement } from 'react-bootstrap/esm/types';

import { FavoriteFilledIcon, FavoriteIcon } from '../../icons';
import Tooltip from '../Tooltip';

import styles from './_index.module.scss';

export type IconButtonProps<T extends ElementType = 'button'> = Omit<
  ComponentPropsWithoutRef<T>,
  'as' | 'children' | 'className'
> & {
  as?: T;
  children?: ReactNode;
  className?: string;
  isDisabled?: boolean;
  placement?: Placement;
  tooltip?: string;
};

type IconButtonComponent = {
  <T extends ElementType = 'button'>(
    props: IconButtonProps<T> & { ref?: IconButtonRef<T> }
  ): null | React.ReactElement;
  Danger: <T extends ElementType = 'button'>(
    props: IconButtonProps<T> & { ref?: IconButtonRef<T> }
  ) => null | React.ReactElement;
  Favorite: <T extends ElementType = 'button'>(
    props: IconButtonFavoriteProps<T> & { ref?: IconButtonRef<T> }
  ) => null | React.ReactElement;
};

type IconButtonFavoriteProps<T extends ElementType = 'button'> = IconButtonProps<T> & {
  isFavorite?: boolean;
};

type IconButtonRef<T extends ElementType> = ComponentPropsWithRef<T>['ref'];

interface IconButtonTooltipProps {
  id: string;
  placement?: Placement;
  target: React.RefObject<HTMLElement | null>;
  tooltip: string;
}

// using this instead of OverlayTrigger from Bootstrap because we need the parentRef usable for IconButtons as part of popovers
// if a delay is necessary to add, reference https://github.com/react-bootstrap/react-bootstrap/blob/master/src/OverlayTrigger.tsx#L177
// and look at the useTimeout() management
const IconButtonTooltip = ({ id, placement, target, tooltip }: IconButtonTooltipProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleHide = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);
  const handleBlur = useCallback(() => {
    handleHide();
  }, [handleHide]);
  const handleFocus = useCallback(() => {
    handleShow();
  }, []);
  const handleMouseOut = useCallback(() => {
    handleHide();
  }, [handleHide]);
  const handleMouseOver = useCallback(() => {
    handleShow();
  }, []);
  const handleShow = useCallback(() => {
    setIsTooltipOpen(true);
  }, []);

  useEffect(() => {
    const element = target.current;

    element?.addEventListener('blur', handleBlur);
    element?.addEventListener('focus', handleFocus);
    element?.addEventListener('mouseout', handleMouseOut);
    element?.addEventListener('mouseover', handleMouseOver);

    return () => {
      element?.removeEventListener('blur', handleBlur);
      element?.removeEventListener('focus', handleFocus);
      element?.removeEventListener('mouseout', handleMouseOut);
      element?.removeEventListener('mouseover', handleMouseOver);
    };
  }, [handleBlur, handleFocus, handleMouseOut, handleMouseOver, target]);

  return (
    <Overlay placement={placement} show={isTooltipOpen} target={target.current}>
      <Tooltip id={id}>{tooltip}</Tooltip>
    </Overlay>
  );
};

const IconButton = forwardRef(function IconButton<T extends ElementType = 'button'>(
  {
    'aria-label': ariaLabel,
    as,
    children,
    className,
    isDisabled,
    placement = 'bottom',
    tooltip,
    ...props
  }: IconButtonProps<T>,
  parentRef: React.ForwardedRef<HTMLElement>
) {
  const localRef = useRef<HTMLElement>(null);
  const tooltipId = useId();
  const Component = (as || 'button') as ElementType;
  const iconButtonRef = parentRef || localRef;
  const accessibleLabel = ariaLabel || (typeof tooltip === 'string' ? tooltip : undefined);

  return (
    <>
      <Component
        aria-describedby={tooltip ? tooltipId : null}
        aria-label={accessibleLabel}
        className={classNames(styles['icon-button'], className)}
        disabled={isDisabled}
        ref={iconButtonRef}
        {...(props as ComponentPropsWithoutRef<ElementType>)}
      >
        {children}
      </Component>
      {tooltip && (
        <IconButtonTooltip
          id={tooltipId}
          placement={placement}
          target={iconButtonRef as React.RefObject<HTMLElement | null>}
          tooltip={tooltip}
        />
      )}
    </>
  );
}) as unknown as IconButtonComponent;

IconButton.Danger = forwardRef(function IconButtonDanger(
  { className, ...props }: IconButtonProps<ElementType>,
  ref: React.ForwardedRef<HTMLElement>
) {
  return (
    <IconButton
      className={classNames(styles['icon-button-danger'], className)}
      ref={ref}
      {...props}
    />
  );
}) as unknown as IconButtonComponent['Danger'];

IconButton.Favorite = forwardRef(function IconButtonFavorite(
  { className, isFavorite, ...props }: IconButtonFavoriteProps<ElementType>,
  ref: React.ForwardedRef<HTMLElement>
) {
  return (
    <IconButton
      className={classNames(styles['icon-button-favorite'], className, {
        [styles['is-favorite']]: isFavorite,
      })}
      ref={ref}
      {...props}
    >
      {isFavorite ? <FavoriteFilledIcon /> : <FavoriteIcon />}
    </IconButton>
  );
}) as unknown as IconButtonComponent['Favorite'];

export default IconButton;
